<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useListenConfigStore } from "@/hooks/useListenConfigStore";
import { imageConfig } from "@/views/listening/components/ThemeTag/imageConfig";
import { themeList } from "./infoConfig";

// Stores & Refs
const listenStore = useListenConfigStore();
const themeVis = ref(false);
const themeVisRef = ref();

// Computed
const theme = computed(() => listenStore.theme);
const imageMap = computed(() => imageConfig[theme.value]);

// Methods
const changeTheme = (themeValue) => {
  listenStore.setTheme(themeValue);
  themeVis.value = false;
};

const toggleThemeVisibility = () => {
  themeVis.value = !themeVis.value;
};

const handleClickOutside = (e) => {
  if(themeVis.value){
    let isSelf = themeVisRef.value.contains(e.target)
    if(!isSelf && e.target.alt !== 'theme'){
      themeVis.value = false
    }
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<template>
  <div class="theme-tag-container">
    <img
      :src="imageMap.icon_theme"
      alt="theme"
      class="theme-trigger"
      @click="toggleThemeVisibility"
    />

    <transition name="theme-popup">
      <div v-if="themeVis" class="theme-popup" ref="themeVisRef">
        <div class="theme-popup-content">
          <button
            v-for="item in themeList"
            :key="item.value"
            class="theme-item"
            :class="{ active: theme === item.value }"
            @click="changeTheme(item.value)"
            :aria-label="`切换至${item.value}主题`"
          >
            <div
              class="theme-item__icon"
              :class="[item.class, theme === item.value ? item.activeClass : '']"
            >
              <div class="theme-item__inner"></div>
            </div>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="less">
@import "@/assets/common.less";

.theme-tag-container {
  position: relative;
  display: inline-block;
}

.theme-trigger {
  width: 1.28rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.theme-popup {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 0.5rem;
  background: #fff;
  border-radius: 0.7rem;
  padding: 0.5rem;
  box-shadow: 0 2px 16px 0 rgba(0, 0, 0, 0.10);
  z-index: 1000;

  &-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

.theme-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    transform: scale(0.95);
  }

  &__icon {
    border-radius: 50%;
    border: 2px solid transparent;
    transition: all 0.2s ease;

    .theme-item__inner {
      width: 1.3rem;
      height: 1.3rem;
      border-radius: 50%;
      transition: all 0.2s ease;
    }
  }
}

// Theme color classes
.green-class {
  .theme-item__inner {
    background: #04976b;
  }

  &-active {
    border-color: rgba(4, 151, 107, 0.24);
  }
}

.white-class {
  .theme-item__inner {
    background: #ffffff;
    border: 2px solid #e5e5e5;
  }

  &-active {
    border-color: transparent;

    .theme-item__inner {
      border-color: #0dcb8b;
    }
  }
}

.brown-class {
  .theme-item__inner {
    background: #b35f34;
  }

  &-active {
    border-color: rgba(179, 95, 52, 0.24);
  }
}

.blue-class {
  .theme-item__inner {
    background: #007aff;
  }

  &-active {
    border-color: rgba(0, 122, 255, 0.24);
  }
}

.dark-class {
  .theme-item__inner {
    background: #1a1d27;
  }

  &-active {
    border-color: rgba(223, 191, 152, 1);
  }
}

// Transition animations
.theme-popup-enter-active,
.theme-popup-leave-active {
  transition: all 0.2s ease;
  transform-origin: top center;
}

.theme-popup-enter-from {
  opacity: 0;
  transform: translateX(-50%) scale(0.95);
}

.theme-popup-leave-to {
  opacity: 0;
  transform: translateX(-50%) scale(0.95);
}
</style>